// Ein Tag hat keine Uhrzeit, und deshalb auch keine Zeitzone.
//
// ORIGINALTEXT MARVIN (10.09.2026, mit 2 Bildschirmfotos)
//   „hier gibt es einen darstellungsfehler. bild 1: von kamil, deutsche
//    zeitzone, bild 2: mein pc, zypern zeitzone. aber das problem sind die
//    wochentage, datum. bitte überprüfen und beheben. warum bekomme ich solche
//    fehler angezeigt?"
//
// SEINE FRAGE IST DIE BESSERE HÄLFTE, und die Antwort lautet: **das Raster hat
// die Tage auf der Uhr des BETRACHTERS gebaut und auf der Uhr des BETRIEBS
// gelesen.**
//
//   `montag()`      `x.setHours(0,0,0,0)`  -> Mitternacht in ZYPERN
//   der Spaltenkopf `d.getDate()`          -> der Tag in ZYPERN
//   die Termine     `tagIso(d)`            -> der Tag in BERLIN
//
// Mitternacht am 07.09. in Zypern ist `2026-09-06T21:00:00Z`, und das ist in
// Berlin der **06.09. um 23:00**. Unter der Beschriftung „Montag 07.09."
// standen deshalb die Termine vom Sonntag - jeder Termin eine Spalte zu weit
// links, und der Sonntag fiel hinten heraus.
//
// > **Ein Wert, den 2 Uhren lesen, ist 2 Werte.** Solange beide Uhren
// > dieselbe Zeit zeigen, ist es EIN Fehler, den niemand sieht; er wird erst
// > sichtbar, wenn jemand woanders sitzt.
//
// UND DESHALB HAT IHN KEIN PRÜFSTAND GEFUNDEN: sie laufen alle mit
// `TZ=Europe/Berlin`, also in derselben Zone wie das Produkt. **Ein Prüfstand,
// der in derselben Zone läuft wie das, was er prüft, kann diesen Fehler nicht
// finden.** Prüfstand 142 läuft deshalb ausdrücklich in Marvins Zone.
//
// DIE BEHEBUNG NIMMT DIE UHR HERAUS, statt eine der beiden zur richtigen zu
// erklären. Ein Kalendertag ist „2026-09-07" und sonst nichts: keine Stunde,
// keine Zone, kein `Date`. Beschriftung und Datenzugriff kommen aus DERSELBEN
// Zeichenkette, und was aus einer Zeichenkette kommt, kann nicht auseinander-
// laufen.
//
// DIESE DATEI TRÄGT KEIN JSX. Sie lässt sich laden und messen; eine Textsuche
// im Bildschirmbaustein misst, dass etwas dasteht, nicht, dass es stimmt.

/** Ein Kalendertag, JJJJ-MM-TT. Ohne Uhrzeit, ohne Zone. */
export type Tag = string;

const TAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const TAGE_LANG = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag',
  'Sonntag',
];

/** Ist das eine Zeichenkette, die wie ein Kalendertag aussieht? */
export function istTag(w: unknown): w is Tag {
  return typeof w === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(w);
}

/**
 * Die Tagesnummer seit dem 01.01.1970, rein gerechnet.
 *
 * `Date.UTC` UND NICHT `new Date(...)`. UTC hat keine Sommerzeit und keinen
 * Ortsversatz; die Zahl, die hier herauskommt, ist überall auf der Welt
 * dieselbe. Sie wird NIE angezeigt - sie dient nur dem Weiterzählen.
 */
function tagesnummer(t: Tag): number {
  const j = Number(t.slice(0, 4));
  const m = Number(t.slice(5, 7));
  const d = Number(t.slice(8, 10));
  return Math.floor(Date.UTC(j, m - 1, d) / 86400000);
}

/** Und zurück. */
function ausTagesnummer(n: number): Tag {
  const d = new Date(n * 86400000);
  return `${d.getUTCFullYear()}-`
       + `${String(d.getUTCMonth() + 1).padStart(2, '0')}-`
       + `${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Der Wochentag: 0 ist Montag, 6 ist Sonntag.
 *
 * MONTAG IST 0 UND NICHT 1. `Date.getDay()` fängt am Sonntag an, und jede
 * Stelle in diesem Haus rechnet `(getDay() + 6) % 7` dagegen - eine
 * Umrechnung, die 6-mal dasteht, ist 6-mal zu prüfen.
 */
export function wochentagNr(t: Tag): number {
  /* Der 01.01.1970 war ein Donnerstag, also Nummer 3. */
  return (((tagesnummer(t) + 3) % 7) + 7) % 7;
}

/** „Mo", „Di", … */
export function wochentagKurz(t: Tag): string {
  return TAGE_KURZ[wochentagNr(t)] ?? '';
}

/** „Montag", „Dienstag", … */
export function wochentag(t: Tag): string {
  return TAGE_LANG[wochentagNr(t)] ?? '';
}

/** „07.09." - mit genau EINEM Punkt am Ende, nicht mit zweien. */
export function kurzTag(t: Tag): string {
  return istTag(t) ? `${t.slice(8, 10)}.${t.slice(5, 7)}.` : '';
}

/** „07.09.2026" - die Form, in der jeder in diesem Haus ein Datum liest. */
export function langTag(t: Tag): string {
  return istTag(t) ? `${t.slice(8, 10)}.${t.slice(5, 7)}.${t.slice(0, 4)}` : '';
}

/** Das Jahr als Zahl. */
export function jahrVon(t: Tag): number {
  return istTag(t) ? Number(t.slice(0, 4)) : 0;
}

/** Ein paar Tage weiter (oder zurück). */
export function plusTage(t: Tag, n: number): Tag {
  return ausTagesnummer(tagesnummer(t) + n);
}

/** Der Montag der Woche, in der dieser Tag liegt. */
export function montagVon(t: Tag): Tag {
  return plusTage(t, -wochentagNr(t));
}

/** Die Tage ab einem Anfang, aufsteigend. */
export function tageAb(anfang: Tag, wieviele: number): Tag[] {
  const raus: Tag[] = [];
  for (let i = 0; i < Math.max(0, wieviele); i++) raus.push(plusTage(anfang, i));
  return raus;
}

/** Liegt dieser Tag auf einem Samstag oder Sonntag? */
export function istWochenende(t: Tag): boolean {
  return wochentagNr(t) >= 5;
}

/**
 * Die Kalenderwoche nach ISO 8601.
 *
 * OHNE JEDE UHR. Die Fassung in `api.ts` baut 4 `Date`-Objekte über die
 * lokalen Getter; sie liefert in Zypern und in Berlin dasselbe, SOLANGE der
 * hineingegebene Tag in beiden Zonen derselbe ist - und genau das war er
 * nicht. Hier gibt es nichts, was auseinanderlaufen könnte.
 */
export function kwVon(t: Tag): number {
  /* Der Donnerstag DIESER Woche entscheidet, zu welchem Jahr sie zählt. */
  const donnerstag = plusTage(t, 3 - wochentagNr(t));
  const jahr = jahrVon(donnerstag);
  const erster = `${jahr}-01-04`;
  const ersterDonnerstag = plusTage(erster, 3 - wochentagNr(erster));
  return 1 + Math.round(
    (tagesnummer(donnerstag) - tagesnummer(ersterDonnerstag)) / 7);
}

/** Das Jahr, zu dem die Kalenderwoche zählt. Der 31.12.2025 liegt in KW 1/2026. */
export function kwJahrVon(t: Tag): number {
  return jahrVon(plusTage(t, 3 - wochentagNr(t)));
}

/**
 * Der heutige Tag - in der Zeit des BETRIEBS und nicht in der des Betrachters.
 *
 * DIESE EINE STELLE DARF EINE UHR LESEN, weil „heute" wirklich eine Frage an
 * eine Uhr ist. Sie fragt die des Betriebs: wer in Zypern um 00:30 Ortszeit
 * auf den Plan sieht, arbeitet in einem Betrieb, in dem noch der Vortag läuft,
 * und der Plan hat ihm den Tag zu zeigen, an dem die Leute draussen stehen.
 */
export function heuteTag(zone = 'Europe/Berlin', jetzt: Date = new Date()): Tag {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(jetzt);
}

/**
 * Ein Wert aus der Antwort des Servers als Tag.
 *
 * EIN BLANKER TAG BLEIBT UNANGETASTET. „2026-09-07" durch eine Uhr zu
 * schicken, ist genau der Fehler, um den es hier geht; er hat keine Zone, und
 * es gibt nichts umzurechnen. Ein ZEITPUNKT dagegen (mit Uhrzeit und Versatz)
 * wird in der Zeit des Betriebs gelesen.
 */
export function alsTag(w: unknown, zone = 'Europe/Berlin'): Tag | '' {
  if (w === null || w === undefined || w === '') return '';
  if (typeof w === 'string') {
    if (istTag(w.slice(0, 10)) && !/T|\s\d\d:/.test(w)) return w.slice(0, 10);
    /* NUR EIN ZEITPUNKT, DER WIE EINER AUSSIEHT.
       Hier stand `new Date(w)` als letzter Versuch, und genau das ist die
       Falle, die `regeln/zeit.ts` beschreibt: `new Date('Tue Aug 11')` wirft
       nicht, sondern liefert den 11.08.2001. **Ein Jahr, das niemand
       geschrieben hat, sieht auf einem Bildschirm aus wie ein Tippfehler von
       uns - und ist einer.** */
    if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(w)) return '';
    const d = new Date(w);
    return Number.isNaN(d.getTime()) ? '' : heuteTag(zone, d);
  }
  if (w instanceof Date) {
    return Number.isNaN(w.getTime()) ? '' : heuteTag(zone, w);
  }
  return '';
}

/**
 * Wie viele Kalendertage zwischen 2 Tagen liegen.
 *
 * IN TAGEN UND NICHT IN STUNDEN. Wer `(b - a) / 86400000` rechnet, bekommt
 * am Ende der Sommerzeit 23,96 Tage statt 24 - und `Math.round` verschluckt
 * das mal und mal nicht. **Eine Frist gegenüber einer Behörde, die je nach
 * Standort des Rechners einen Tag anders ausfällt**, ist genau die Sorte
 * Fehler, die niemand für möglich hält, bis sie eintritt.
 */
export function tageBis(von: Tag, bis: Tag): number {
  if (!istTag(von) || !istTag(bis)) return 0;
  return tagesnummer(bis) - tagesnummer(von);
}

// ----------------------------------------------------------------- Der Sucher

/**
 * Die Uhr des BETRACHTERS, in einer Zeile Quelltext erkannt.
 *
 * DIESE MUSTER SIND AN ECHTEN FUNDEN GESCHLIFFEN und nicht ausgedacht. Am
 * 10.09.2026 standen 40 solcher Stellen in 6 Dateien, und alle 6 zeigten
 * einem Betrachter ausserhalb Deutschlands einen anderen Tag als dem
 * Innendienst.
 */
export const LOKALE_UHR =
  /\.(getDate|getMonth|getFullYear|getDay|getHours|setDate|setHours|setMonth|setFullYear)\s*\(/;

/**
 * Was BELEGT ist und deshalb nicht gemeldet wird.
 *
 * UTC-METHODEN SIND SICHER: `Date.UTC`, `getUTCDate`, `toISOString` auf einem
 * UTC-Zeitpunkt rechnen überall gleich. Und `toLocaleDateString` mit einem
 * festen `timeZone` liest die Uhr des Betriebs, nicht die des Betrachters.
 *
 * > **Ein Sucher, der falsch meldet, wird abgeschaltet - und danach meldet er
 * > gar nichts mehr.** Ein Fehlalarm kostet mehr als ein übersehener Fund: er
 * > nimmt alle künftigen mit.
 */
export const ANZEIGE_OHNE_ZONE = /toLocale(Date|Time)String\s*\(/;

export interface Uhrfund { datei: string; zeile: number; grund: string; text: string }

/**
 * Sucht die Uhr des Betrachters in einer Quelltextdatei.
 *
 * KOMMENTARE ZÄHLEN NICHT, auch mehrzeilige nicht: in ihnen steht die
 * GESCHICHTE einer Stelle, und die nennt genau die Aufrufe, um die es geht.
 */
export function sucheLokaleUhr(datei: string, inhalt: string): Uhrfund[] {
  const raus: Uhrfund[] = [];
  const zeilen = inhalt.split('\n');
  let imBlock = false;
  zeilen.forEach((z, i) => {
    if (imBlock) { if (z.includes('*/')) imBlock = false; return; }
    /* AUCH EIN JSX-KOMMENTAR IST EINER. `{/* … *\/}` faengt nicht mit `/*`
       an, und der erste Entwurf hat deshalb einen Kommentar gemeldet, der
       genau diese Falle ERKLAERT - **ein Sucher, der den Bericht ueber einen
       behobenen Fehler fuer den Fehler haelt, meldet die Behebung.** */
    if (/^\s*\{?\s*\/\*/.test(z)) { if (!z.includes('*/')) imBlock = true; return; }
    if (/^\s*(\/\/|\*)/.test(z)) return;

    if (LOKALE_UHR.test(z)) {
      raus.push({ datei, zeile: i + 1, grund: 'lokale Getter', text: z.trim() });
      return;
    }
    if (ANZEIGE_OHNE_ZONE.test(z)) {
      /* Der `timeZone` darf ein paar Zeilen tiefer stehen: die Aufrufe in
         diesem Haus sind über 3 Zeilen umbrochen. */
      const umfeld = zeilen.slice(i, i + 5).join('\n');
      if (!umfeld.includes('timeZone')) {
        raus.push({ datei, zeile: i + 1, grund: 'toLocale ohne timeZone',
                    text: z.trim() });
      }
    }
  });
  return raus;
}
